import { crudUtil } from "./axiosCRUD";
import logger from "../utils/logger";
import { RECORDING_FLAGS } from "../constants/recordingFlags";

export interface BlankRecordingInitiateResponse {
  recordingId: string;
  uploadUrls: {
    screen: { url: string; key: string };
    webcam: { url: string; key: string };
    background?: { url: string; key: string };
  };
}

interface BackendInitiateResponse {
  success: boolean;
  recordingId: string;
  basePath: string;
  presignedUrls: {
    screen: { uploadUrl: string; key: string };
    webcam: { uploadUrl: string; key: string };
    background?: { uploadUrl: string; key: string };
  };
}

// Stream segment for tracking on/off times during recording
// Each segment corresponds to a separate video file
export interface StreamSegment {
  segmentIndex: number; // Index of this segment (0, 1, 2, ...)
  tsStart: number; // Start time in milliseconds from recording start
  tsEnd: number | null; // End time in ms, null if still active at end
  hasAudio: boolean; // Whether this segment carried audio
  key?: string; // S3 key for this segment's video file (set after upload)
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
  // Position at the time this segment started (for layout recomposition)
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  // Shape properties for webcam (circular vs rectangular)
  isCircle?: boolean; // true if webcam is circular/rounded
  cornerRadius?: number; // corner radius in pixels (0 for rectangular, >0 for rounded corners)
  // Mirror properties for webcam
  isMirrored?: boolean; // true if webcam is horizontally mirrored (flipX)
  // Avatar properties (when using static image instead of video)
  isAvatar?: boolean; // true if this segment displays an avatar image instead of video
  avatarUrl?: string; // URL of the avatar image (when isAvatar is true)
}

// Audio segment for dedicated audio stream (separate from video)
export interface AudioStreamSegment {
  segmentIndex: number; // Index of this segment (0, 1, 2, ...)
  tsStart: number; // Start time in milliseconds from recording start
  tsEnd: number | null; // End time in ms, null if still active at end
  key?: string; // S3 key for this segment's audio file (set after upload)
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
  hasMic: boolean; // Whether this segment contains microphone audio
  hasSystemAudio: boolean; // Whether this segment contains system audio
  isMuted: boolean; // Whether this segment is intentionally silent
}

// Stream layout with segments for mid-recording toggles
export interface StreamLayoutWithSegments {
  x: number;
  y: number;
  width: number;
  height: number;
  visible: boolean;
  segments?: StreamSegment[]; // Array of on/off segments during recording
}

export interface BlankRecordingLayout {
  canvas: {
    width: number;
    height: number;
    aspectRatio?: string;
    quality?: string;
  };
  webcam?: StreamLayoutWithSegments;
  screen?: StreamLayoutWithSegments;
  background: {
    type: 'color' | 'image' | 'gradient';
    value?: string;
    imageKey?: string;
  };
}

// Single stream info for simple recordings (no mid-recording toggles)
export interface StreamInfo {
  key: string;
  hasAudio: boolean;
}

// Stream with multiple segments for mid-recording toggles
export interface StreamWithSegments {
  segments: StreamSegment[]; // Array of segments, each with its own video key
}

// Audio stream with multiple segments for audio source toggles (mic mute/unmute, system audio changes)
export interface AudioStreamWithSegments {
  segments: AudioStreamSegment[]; // Array of segments, each with its own audio key
}

export interface BlankRecordingCompleteRequest {
  recordingId: string;
  streams: {
    // Support both simple (single key) and multi-segment formats
    screen?: StreamInfo | StreamWithSegments;
    webcam?: StreamInfo | StreamWithSegments;
    audio?: AudioStreamWithSegments; // Dedicated audio stream (separate from video)
  };
  layout: BlankRecordingLayout;
  duration: number;
  micEnabled?: boolean;
  hasDedicatedAudioStream?: boolean; // Whether this recording has a separate audio stream
}

export interface BlankRecordingCompleteResponse {
  success: boolean;
  recordingId: string;
  databaseId: number;
  hlsStatus: string;
  message?: string;
}

export interface BlankRecording {
  recordingId: string;
  databaseId: number;
  metadata: {
    isBlankMode: boolean;
    recordingType: string;
    layout: BlankRecordingLayout;
    audio: {
      screenHasAudio: boolean;
      webcamHasAudio: boolean;
      micEnabled: boolean;
    };
    capturedAt: string;
    version: string;
  };
  hlsStatus: string;
  duration: number;
  createdAt: string;
  streams?: {
    screen?: { mp4Url: string; hlsUrl: string | null };
    webcam?: { mp4Url: string; hlsUrl: string | null };
  };
  backgroundUrl?: string;
}

class BlankRecordingService {
  private isEnabled(): boolean {
    return RECORDING_FLAGS.SEPARATE_STREAMS_MODE;
  }

  async initiate(includeBackground: boolean = false): Promise<BlankRecordingInitiateResponse> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    logger.info('[BlankRecordingService] Initiating blank recording');
    
    const response = await crudUtil.post<BackendInitiateResponse>('/api/recordings/blank/initiate', {
      includeBackground,
    });

    logger.info('[BlankRecordingService] Received upload URLs', {
      recordingId: response.recordingId,
      hasScreenUrl: !!response.presignedUrls?.screen,
      hasWebcamUrl: !!response.presignedUrls?.webcam,
      hasBackgroundUrl: !!response.presignedUrls?.background,
    });

    return {
      recordingId: response.recordingId,
      uploadUrls: {
        screen: {
          url: response.presignedUrls.screen.uploadUrl,
          key: response.presignedUrls.screen.key,
        },
        webcam: {
          url: response.presignedUrls.webcam.uploadUrl,
          key: response.presignedUrls.webcam.key,
        },
        background: response.presignedUrls.background ? {
          url: response.presignedUrls.background.uploadUrl,
          key: response.presignedUrls.background.key,
        } : undefined,
      },
    };
  }

  // Get presigned upload URL for a specific video segment
  async getSegmentUploadUrl(
    recordingId: string,
    streamType: 'screen' | 'webcam',
    segmentIndex: number
  ): Promise<{ url: string; key: string }> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    logger.info('[BlankRecordingService] Getting segment upload URL', {
      recordingId,
      streamType,
      segmentIndex,
    });

    const response = await crudUtil.post<{
      success: boolean;
      uploadUrl: string;
      key: string;
    }>('/api/recordings/blank/segment-url', {
      recordingId,
      streamType,
      segmentIndex,
    });

    return {
      url: response.uploadUrl,
      key: response.key,
    };
  }

  // Get presigned upload URL for an audio segment
  async getAudioSegmentUploadUrl(
    recordingId: string,
    segmentIndex: number
  ): Promise<{ url: string; key: string }> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    logger.info('[BlankRecordingService] Getting audio segment upload URL', {
      recordingId,
      segmentIndex,
    });

    const response = await crudUtil.post<{
      success: boolean;
      uploadUrl: string;
      key: string;
    }>('/api/recordings/blank/segment-url', {
      recordingId,
      streamType: 'audio',
      segmentIndex,
    });

    return {
      url: response.uploadUrl,
      key: response.key,
    };
  }

  async complete(request: BlankRecordingCompleteRequest): Promise<BlankRecordingCompleteResponse> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    logger.info('[BlankRecordingService] Completing blank recording', {
      recordingId: request.recordingId,
      duration: request.duration,
      hasScreen: !!request.streams.screen,
      hasWebcam: !!request.streams.webcam,
    });

    const response = await crudUtil.post('/api/recordings/blank/complete', request);

    logger.info('[BlankRecordingService] Recording completed', {
      recordingId: response.recordingId,
      databaseId: response.databaseId,
      hlsStatus: response.hlsStatus,
    });

    return response;
  }

  async get(recordingId: string): Promise<BlankRecording> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    const response = await crudUtil.get(`/api/recordings/blank/${recordingId}`);
    return response;
  }

  async list(limit: number = 50, offset: number = 0): Promise<{ recordings: BlankRecording[]; total: number }> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    const response = await crudUtil.get(`/api/recordings/blank?limit=${limit}&offset=${offset}`);
    return response;
  }

  async delete(recordingId: string): Promise<{ success: boolean }> {
    if (!this.isEnabled()) {
      throw new Error('Separate streams mode is not enabled');
    }

    logger.info('[BlankRecordingService] Deleting blank recording', { recordingId });
    
    const response = await crudUtil.delete(`/api/recordings/blank/${recordingId}`);
    
    logger.info('[BlankRecordingService] Recording deleted', { recordingId });
    
    return response;
  }
}

export const blankRecordingService = new BlankRecordingService();
